import { useEffect, useState } from 'react';
import {
	Button,
	Card,
	Flex,
	InputNumber,
	Space,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Typography,
	notification,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import {
	PROCESS_KINDS,
	PROCESS_LABELS,
	type ProcessKind,
	type ProcessSpec,
} from '../../../../interfaces/ProcessCatalog.Interface';
import {
	useGetProcessMinBatchQuery,
	useGetProcessSpecsQuery,
	useSetProcessMinBatchMutation,
} from '../../../../redux/api/processCatalog';
import { extractApiError } from '../../../../utils/apiError';
import ProcessSpecFormModal from './ProcessSpecFormModal';

const { Text, Paragraph } = Typography;

/**
 * The finishing / handling / cleaning catalog, plus the batching minimum.
 *
 * One screen for all three because they behave identically once configured;
 * only how a job gets one differs (finishing is matched from the surface
 * finish, the other two are picked).
 */
export default function ProcessCatalogCard() {
	const [kind, setKind] = useState<ProcessKind>('finishing');
	const [editing, setEditing] = useState<ProcessSpec | null>(null);
	const [modalOpen, setModalOpen] = useState(false);

	const { data: specs = [], isFetching } = useGetProcessSpecsQuery({ kind });

	function openNew() {
		setEditing(null);
		setModalOpen(true);
	}

	function openEdit(spec: ProcessSpec) {
		setEditing(spec);
		setModalOpen(true);
	}

	return (
		<Card
			title="Finishing, Handling & Cleaning"
			extra={
				<Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
					New {PROCESS_LABELS[kind]}
				</Button>
			}
		>
			<Paragraph type="secondary" style={{ marginTop: 0 }}>
				A <Text strong>Special process</Text> row prints its own tag during
				kitting, highlights the job on the shop floor tablet and creates an
				operator task once each batch of good parts is logged. A{' '}
				<Text strong>Do nothing</Text> row is invisible everywhere — that is
				the right setting for common finishes and for Standard handling and
				cleaning.
			</Paragraph>

			<MinBatchField />

			<Tabs
				activeKey={kind}
				onChange={(key) => setKind(key as ProcessKind)}
				items={PROCESS_KINDS.map((processKind) => ({
					key: processKind,
					label: PROCESS_LABELS[processKind],
					children: (
						<SpecTable
							kind={processKind}
							specs={processKind === kind ? specs : []}
							loading={processKind === kind && isFetching}
							onEdit={openEdit}
						/>
					),
				}))}
			/>

			<ProcessSpecFormModal
				open={modalOpen}
				kind={kind}
				spec={editing}
				onClose={() => setModalOpen(false)}
			/>
		</Card>
	);
}

/** The one global number that turns a quality interval into batch sizes. */
function MinBatchField() {
	const { data, isLoading } = useGetProcessMinBatchQuery();
	const [setMinBatch, { isLoading: isSaving }] = useSetProcessMinBatchMutation();
	const [value, setValue] = useState<number | null>(null);

	useEffect(() => {
		if (data) setValue(data.min_batch);
	}, [data]);

	const dirty = data != null && value != null && value !== data.min_batch;

	async function save() {
		if (value == null) return;
		try {
			await setMinBatch(value).unwrap();
			notification.success({ message: 'Minimum batch saved' });
		} catch (error) {
			notification.error({ message: extractApiError(error) });
		}
	}

	return (
		<Flex align="center" gap={12} className="mb-4" wrap>
			<Tooltip title="How few parts are worth stopping to run a batch. A job's quality interval sets how often a batch comes due; this stops a long job from producing dozens of one-part batches. Applies to every batched process.">
				<Text strong>Minimum batch</Text>
			</Tooltip>
			<InputNumber
				min={1}
				value={value}
				disabled={isLoading}
				onChange={setValue}
				style={{ width: 100 }}
			/>
			<Text type="secondary">parts</Text>
			{dirty && (
				<Button type="primary" loading={isSaving} onClick={save}>
					Save
				</Button>
			)}
		</Flex>
	);
}

function SpecTable({
	kind,
	specs,
	loading,
	onEdit,
}: {
	kind: ProcessKind;
	specs: ProcessSpec[];
	loading: boolean;
	onEdit: (spec: ProcessSpec) => void;
}) {
	return (
		<Table<ProcessSpec>
			rowKey="id"
			size="small"
			loading={loading}
			dataSource={specs}
			pagination={false}
			onRow={(spec) => ({
				onClick: () => onEdit(spec),
				style: { cursor: 'pointer' },
			})}
			columns={[
				{
					title: 'Name',
					dataIndex: 'name',
					render: (name: string, spec) => (
						<Space>
							<Text strong={spec.is_special}>{name}</Text>
							{spec.is_default && <Tag color="blue">Default</Tag>}
							{!spec.is_active && <Tag>Retired</Tag>}
						</Space>
					),
				},
				{
					title: 'Behavior',
					dataIndex: 'behavior',
					render: (_behavior, spec) =>
						spec.is_special ? (
							<Tag color="orange">Special process</Tag>
						) : (
							<Text type="secondary">Do nothing</Text>
						),
				},
				{
					title: 'Tags',
					dataIndex: 'batch_mode',
					render: (_mode, spec) => {
						if (spec.batch_mode === 'quality_interval' && spec.prints_tags) {
							return <Text>One per batch</Text>;
						}
						if (spec.batch_mode === 'one_tag' && spec.prints_tags) {
							return <Text>One tag</Text>;
						}
						return <Text type="secondary">None</Text>;
					},
				},
				...(kind === 'finishing'
					? [
							{
								title: 'Matches surface finish',
								dataIndex: 'match_tokens',
								render: (tokens: string[]) =>
									tokens.length > 0 ? (
										<Space size={4} wrap>
											{tokens.map((token) => (
												<Tag key={token}>{token}</Tag>
											))}
										</Space>
									) : (
										<Text type="secondary">—</Text>
									),
							},
					  ]
					: []),
				{
					title: 'Instructions',
					dataIndex: 'instructions',
					ellipsis: true,
					render: (instructions: string) =>
						instructions ? (
							<Tooltip title={instructions}>{instructions}</Tooltip>
						) : (
							<Text type="secondary">—</Text>
						),
				},
			]}
		/>
	);
}
